import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";
import PropTypes from "prop-types";
import { Button } from "@boozallen-uip/uswds-react-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faChevronDown, faFileCode } from "@fortawesome/free-solid-svg-icons";
import downloadAcrYaml from "../utils/acrExport/downloadAcrYaml";
import downloadAcrHtml from "../utils/acrExport/downloadAcrHtml";
import downloadAcrJson from "../utils/acrExport/downloadAcrJson";

/**
 * Shared “Download ▼” control for ACR exports (YAML, HTML, JSON).
 * Used on the editor Report page and on the public product detail page.
 * Follows the menu button pattern (APG): role="menu", role="menuitem", arrow keys, Escape, Tab closes.
 */
export default function AcrDownloadMenu({
  evaluation,
  catalog,
  enabled = true,
  menuButtonId = "acr-download-menu-button",
  menuId = "acr-download-menu",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const menuItemRefs = useRef([]);
  /** "first" | "last" — where to place focus when menu opens */
  const pendingFocusRef = useRef("first");

  const downloadActions = useMemo(
    () => [
      { id: "yaml", label: "Download YAML", onClick: () => downloadAcrYaml(evaluation, catalog) },
      { id: "html", label: "Download HTML", onClick: () => downloadAcrHtml(evaluation, catalog) },
      { id: "json", label: "Download JSON", onClick: () => downloadAcrJson(evaluation, catalog) },
    ],
    [evaluation, catalog]
  );

  const focusTrigger = useCallback(() => {
    queueMicrotask(() => {
      const el = buttonRef.current
        ?? (typeof document !== "undefined" ? document.getElementById(menuButtonId) : null);
      el?.focus?.();
    });
  }, [menuButtonId]);

  const closeMenu = useCallback((returnFocusToTrigger) => {
    setIsOpen(false);
    if (returnFocusToTrigger) {
      focusTrigger();
    }
  }, [focusTrigger]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        closeMenu(true);
      }
    };
    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, closeMenu]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const target = pendingFocusRef.current;
    pendingFocusRef.current = "first";
    const raf = requestAnimationFrame(() => {
      const items = menuItemRefs.current.filter(Boolean);
      if (items.length === 0) return;
      const el = target === "last" ? items[items.length - 1] : items[0];
      el.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  useEffect(() => {
    if (!enabled && isOpen) {
      closeMenu(true);
    }
  }, [enabled, isOpen, closeMenu]);

  const handleMenuKeyDown = useCallback((event) => {
    const items = menuItemRefs.current.filter(Boolean);
    if (!items.length) return;

    const active = document.activeElement;
    let idx = items.indexOf(active);
    if (idx < 0) idx = 0;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        items[(idx + 1) % items.length].focus();
        break;
      case "ArrowUp":
        event.preventDefault();
        items[(idx - 1 + items.length) % items.length].focus();
        break;
      case "Home":
        event.preventDefault();
        items[0].focus();
        break;
      case "End":
        event.preventDefault();
        items[items.length - 1].focus();
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        closeMenu(true);
        break;
      case "Tab":
        event.preventDefault();
        closeMenu(true);
        break;
      default:
        break;
    }
  }, [closeMenu]);

  const handleTriggerKeyDown = useCallback((event) => {
    if (!enabled) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        pendingFocusRef.current = event.key === "ArrowDown" ? "first" : "last";
        setIsOpen(true);
      }
    } else if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      closeMenu(true);
    }
  }, [enabled, isOpen, closeMenu]);

  const runAction = useCallback((action) => {
    action.onClick();
    setStatusMessage("");
    setTimeout(() => {
      setStatusMessage(`${action.label} started.`);
    }, 0);
    closeMenu(true);
  }, [closeMenu]);

  return (
    <div className="inline-block">
      <div className="relative inline-block" ref={menuRef}>
        <Button
          ref={buttonRef}
          id={menuButtonId}
          type="button"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={menuId}
          disabled={!enabled}
          onClick={() => {
            if (isOpen) {
              closeMenu(true);
            } else {
              pendingFocusRef.current = "first";
              setIsOpen(true);
            }
          }}
          onKeyDown={handleTriggerKeyDown}
          className="inline-flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faDownload} aria-hidden="true" />
          <span>Download</span>
          <FontAwesomeIcon
            icon={faChevronDown}
            aria-hidden="true"
            className={`text-xs transition-transform duration-200 ease-out ${isOpen ? "rotate-180" : ""}`}
          />
        </Button>
        {isOpen && (
          <div
            id={menuId}
            role="menu"
            tabIndex={-1}
            aria-labelledby={menuButtonId}
            onKeyDown={handleMenuKeyDown}
            className="absolute left-0 mt-2 min-w-[14rem] overflow-hidden rounded-lg border border-gray-lighter bg-white py-1 shadow-lg z-20"
          >
            {downloadActions.map((action, index) => (
              <button
                key={action.id}
                ref={(el) => {
                  menuItemRefs.current[index] = el;
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                className="flex w-full appearance-none cursor-pointer items-center gap-3 border-0 bg-transparent px-4 py-2.5 text-left text-sm text-gray-darkest transition-colors hover:bg-gray-lightest focus:bg-gray-lightest focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#2491ff] focus-visible:outline-offset-[-2px]"
                onMouseEnter={() => menuItemRefs.current[index]?.focus()}
                onClick={() => runAction(action)}
              >
                <FontAwesomeIcon icon={faFileCode} aria-hidden="true" className="text-gray" />
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div aria-live="polite" aria-atomic="true" className="usa-sr-only">
        {statusMessage}
      </div>
    </div>
  );
}

AcrDownloadMenu.propTypes = {
  evaluation: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
  enabled: PropTypes.bool,
  menuButtonId: PropTypes.string,
  menuId: PropTypes.string,
};
